// Compare page — small display helpers shared by the selection bar and grids.

import { formatDetailedPositions } from "../../utils/positionFormatter";
import { formatFeeAmount } from "../../utils/scoutFeeFrame";
import { CURRENCY_SYMBOLS } from "../../utils/currencyFormatter";

const DEF_CODES = new Set(["DF", "DEF", "CB", "LB", "RB", "RWB", "LWB", "Defender"]);
const MID_CODES = new Set(["MF", "MID", "CM", "DM", "AM", "Midfielder"]);
const GK_CODES = new Set(["GK", "GKP", "Goalkeeper", "G"]);

/**
 * `tp_detailed_position` arrives as an OBJECT from both the search endpoint
 * and GET /players/public/{id}/ ({primary_position, secondary_position,
 * teritory_position}) — rendering it raw crashes React ("Objects are not
 * valid as a React child"). Resolve it through the platform's canonical
 * formatter (utils/positionFormatter.js) and take the primary code for the
 * chip, the same reading as PlayersGrid.js:233. Legacy string values pass
 * through unchanged.
 */
export const primaryDetailedPosition = (value) => {
  if (!value) return null;
  if (typeof value === "string") return value.trim() || null;
  // Empty-string fallback: `null` would coalesce to the formatter's "-".
  const formatted = formatDetailedPositions(value, "");
  // formatDetailedPositions joins "primary / secondary / tertiary" — chips
  // show one short code, so keep the first (= primary, or the best fallback).
  return formatted ? formatted.split(" / ")[0] : null;
};

/** Short position label for chips: detailed position first, then role codes. */
export const positionLabel = (p) =>
  primaryDetailedPosition(p?.tp_detailed_position) ||
  p?.positionCode ||
  p?.role?.code2 ||
  p?.role?.code3 ||
  p?.position ||
  null;

/** Chip tint per position family (design prototype `role()`). */
export const positionChipStyle = (p) => {
  const candidates = [
    p?.role?.code2,
    p?.role?.code3,
    p?.role?.name,
    p?.positionCode,
    primaryDetailedPosition(p?.tp_detailed_position),
    p?.position,
  ].filter(Boolean);
  if (candidates.some((c) => GK_CODES.has(c))) {
    return { background: "#FBF1E2", color: "#96631A" };
  }
  if (candidates.some((c) => DEF_CODES.has(c))) {
    return { background: "#ECECFC", color: "#4B4BC7" };
  }
  if (candidates.some((c) => MID_CODES.has(c))) {
    return { background: "#E4F6F3", color: "#0B8A7C" };
  }
  return { background: "#EAF3FF", color: "#1B6FD6" };
};

/**
 * "Jun 2028" — ONLY when the contract expiry lies in the future; otherwise
 * null and the header omits the line entirely (brief data rule 4).
 */
export const contractLabel = (dateString) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime()) || date.getTime() <= Date.now()) return null;
  return date.toLocaleDateString("en-GB", { month: "short", year: "numeric" });
};

/** Capitalised preferred foot ("left" -> "Left"); null-safe. */
export const footLabel = (foot) => {
  const f = String(foot || "").trim();
  if (!f) return null;
  return f.charAt(0).toUpperCase() + f.slice(1).toLowerCase();
};

/**
 * "€12M" / "€850k" — current market value chip (Marin 2026-08-14 lifts the
 * old "no MV" brief rule; TheSports supplies it and the public profile
 * endpoint passes it through as market_value {value, currency}).
 *
 * Reuses the platform's canonical compact-amount formatter
 * (utils/scoutFeeFrame.formatFeeAmount — "1.2M"/"800k", trailing ".0"
 * dropped) and the platform symbol table (utils/currencyFormatter
 * CURRENCY_SYMBOLS). A letter-based symbol (CHF) gets a space, same rule as
 * formatCurrencyWithSymbol. Returns null unless value > 0 — the chip is
 * simply omitted, never "—".
 */
export const marketValueLabel = (mv) => {
  const value = Number(mv?.value);
  if (!Number.isFinite(value) || value <= 0) return null;
  const code = String(mv.currency || "EUR").trim().toUpperCase();
  const sym = CURRENCY_SYMBOLS[code] || code;
  const sep = /[A-Za-z]$/.test(sym) ? " " : "";
  return `${sym}${sep}${formatFeeAmount(value)}`;
};

/** Map a platform search row to the page's player view-model. */
export const searchRowToPlayer = (row) => ({
  id: row.wyscout_id,
  name: row.short_name || row.name,
  fullName: row.name || row.short_name,
  imageUrl: row.image_url || null,
  club: row.club?.name || null,
  clubImage: row.club_image || null,
  position: row.position || null,
  positionCode: row.position_code2 || row.position_code3 || null,
  tp_detailed_position: row.tp_detailed_position || null,
  age: row.age ?? null,
  nationalities: row.nationalities || null,
  nationality: row.nationality || null,
  birth_country: row.birth_country || null,
});
